import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { fmtErr } from "@/lib/api";

const UNITS = ["KG", "MT", "TRUCK"];

export default function AdminAssistedOrderDialog({ open, onClose, customer, onCreated }) {
  const [products, setProducts] = useState([]);
  const [rows, setRows] = useState([{ product_id: "", qty: "", unit: "MT" }]);
  const [deliveryDate, setDeliveryDate] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRows([{ product_id: "", qty: "", unit: "MT" }]);
    setDeliveryDate("");
    setNotes("");
    setLoading(true);
    api.get("/admin/products")
      .then((r) => setProducts((r.data || []).filter((p) => p.enabled !== false)))
      .catch((e) => toast.error(fmtErr(e)))
      .finally(() => setLoading(false));
  }, [open]);

  const updateRow = (index, field, value) => {
    setRows((old) =>
      old.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const addRow = () => {
    setRows((old) => [...old, { product_id: "", qty: "", unit: "MT" }]);
  };

  const removeRow = (index) => {
    setRows((old) => old.filter((_, i) => i !== index));
  };

  const placeOrder = async () => {
    if (!customer?.id) {
      toast.error("Customer ID is missing");
      return;
    }

    const items = rows.map((r) => ({
      product_id: r.product_id,
      qty: Number(r.qty),
      unit: r.unit,
    }));

    if (!items.length || items.some((i) => !i.product_id || !i.qty || i.qty <= 0)) {
      toast.error("Select a product and enter a valid quantity");
      return;
    }

    if (new Set(items.map((i) => i.product_id)).size !== items.length) {
      toast.error("Please select each product only once");
      return;
    }

    try {
      setSaving(true);
      const { data } = await api.post("/admin/orders/create", {
        customer_id: customer.id,
        items,
        preferred_delivery_date: deliveryDate || null,
        notes: notes.trim() || null,
      });

      toast.success(`Order ${data.order_number || "created"} for ${customer.name}`);
      onCreated?.(data);
      onClose();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white text-slate-900 border-slate-300 shadow-2xl">
        <DialogHeader>
          <DialogTitle className="text-xl">
            Create Order for Customer
          </DialogTitle>
        </DialogHeader>

        {customer && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
              Order will be placed on behalf of
            </div>
            <div className="mt-1 grid gap-2 sm:grid-cols-3 text-sm">
              <div>
                <span className="text-slate-500">Customer</span>
                <div className="font-bold">{customer.name}</div>
              </div>
              <div>
                <span className="text-slate-500">Customer ID</span>
                <div className="font-mono font-bold">{customer.id}</div>
              </div>
              <div>
                <span className="text-slate-500">Mobile</span>
                <div className="font-bold">+91 {customer.mobile}</div>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-bold text-slate-900">Products</h3>
              <p className="text-xs text-slate-500">
                Select fertilizer, quantity and unit.
              </p>
            </div>
            <button
              type="button"
              className="sf-btn sf-btn-secondary inline-flex items-center gap-1"
              onClick={addRow}
              disabled={loading || saving}
            >
              <Plus className="h-4 w-4" />
              Add Product
            </button>
          </div>

          {loading ? (
            <div className="rounded-lg border bg-slate-50 p-5 text-center text-sm text-slate-500">
              Loading products...
            </div>
          ) : (
            <div className="space-y-3">
              {rows.map((row, index) => {
                const selectedProduct = products.find((p) => p.id === row.product_id);
                return (
                  <div
                    key={index}
                    className="grid gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 sm:grid-cols-[1fr_120px_130px_42px]"
                  >
                    <div>
                      <label className="sf-label">Product *</label>
                      <select
                        className="sf-input w-full"
                        value={row.product_id}
                        onChange={(e) => updateRow(index, "product_id", e.target.value)}
                        disabled={saving}
                      >
                        <option value="">Select product</option>
                        {products.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.company_name ? `${p.company_name} — ` : ""}
                            {p.name}
                            {p.stock_mt != null ? ` · Stock ${p.stock_mt} MT` : ""}
                          </option>
                        ))}
                      </select>
                      {selectedProduct && (
                        <div className="mt-1 text-xs text-slate-500">
                          Available stock:{" "}
                          <b>{selectedProduct.stock_mt ?? 0} MT</b>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="sf-label">Quantity *</label>
                      <input
                        className="sf-input w-full"
                        type="number"
                        min="0"
                        step="any"
                        value={row.qty}
                        onChange={(e) => updateRow(index, "qty", e.target.value)}
                        disabled={saving}
                        placeholder="0"
                      />
                    </div>

                    <div>
                      <label className="sf-label">Unit *</label>
                      <select
                        className="sf-input w-full"
                        value={row.unit}
                        onChange={(e) => updateRow(index, "unit", e.target.value)}
                        disabled={saving}
                      >
                        {UNITS.map((unit) => (
                          <option key={unit} value={unit}>
                            {unit}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex items-end">
                      <button
                        type="button"
                        className="h-10 w-10 rounded-md border border-rose-200 bg-white text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                        onClick={() => removeRow(index)}
                        disabled={saving || rows.length === 1}
                        title="Remove product"
                      >
                        <Trash2 className="mx-auto h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="sf-label">Preferred Delivery Date</label>
              <input
                className="sf-input w-full"
                type="date"
                value={deliveryDate}
                onChange={(e) => setDeliveryDate(e.target.value)}
                disabled={saving}
              />
            </div>

            <div>
              <label className="sf-label">Admin Note</label>
              <input
                className="sf-input w-full"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={saving}
                placeholder="Optional note"
              />
            </div>
          </div>

          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            This order is being created by an authorized admin on behalf of the
            selected customer. The customer will be notified after the order is
            placed.
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              className="sf-btn sf-btn-secondary flex-1"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="sf-btn sf-btn-primary flex-1"
              onClick={placeOrder}
              disabled={saving || loading}
            >
              {saving ? "Placing Order..." : "Place Order for Customer"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
